import React, { useEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { FaHandPointer } from 'react-icons/fa';
import type { RootState } from '../../store';
import { centerOnNode } from '../../store/workflow/workflowSlice';
import { POINT_EVENT, PointRequest } from '../../help/pointer';

/**
 * 가리키는 손가락 — help/pointer.ts 의 pointAt() 요청을 받아 화면 위에 그린다(앱에 하나).
 *
 *   · 손가락이 대상으로 날아가고(첫 등장은 오른쪽 아래에서), tap 이면 한 번 누르는 동작을 보인다
 *   · 대상 둘레가 빛나고, label 이 있으면 말풍선을 붙인다
 *   · 대상이 움직이면(캔버스 이동·스크롤·패널 크기) 매 프레임 따라간다 — 위치는 React 상태가 아니라 직접 스타일로 옮긴다
 *   · 사라지는 때: hold 가 지남 · 다른 가리키기 · clearPointer · Esc · 사용자가 그 대상을 누름(해 본 것이다)
 * 클릭을 가로채지 않는다(pointer-events: none) — 가리키는 것이지 막는 것이 아니다.
 */

/** 손가락 그림(FaHandPointer, 448×512)에서 검지 끝의 위치 비율 */
const TIP_X = 0.4;
const TIP_Y = 0.02;
const HAND = 34;
const FLY_MS = 550;
const TAP_MS = 420;

type Phase = 'hidden' | 'fly' | 'tap' | 'idle';

function findElement(target: string, store: { getState: () => RootState }): HTMLElement | null {
  if (target.startsWith('node:')) {
    const ref = target.slice(5).trim();
    const nodes = (store.getState() as any).workflow.nodes as any[];
    const n = [...nodes].reverse().find((x) => x.id === ref || String(x.data?.label || '').trim() === ref)
      || [...nodes].reverse().find((x) => String(x.data?.label || '').includes(ref));
    if (!n) return null;
    return document.querySelector<HTMLElement>(`.react-flow__node[data-id="${CSS.escape(n.id)}"]`);
  }
  // 같은 키가 여럿이면(숨은 복제본 등) 보이는 첫 것
  const all = Array.from(document.querySelectorAll<HTMLElement>(`[data-point="${CSS.escape(target)}"]`));
  return all.find((el) => { const r = el.getBoundingClientRect(); return r.width > 0 && r.height > 0; }) || null;
}

/** 캔버스 노드가 캔버스 화면 밖이면 가운데로 옮긴다 */
function ensureNodeVisible(target: string, el: HTMLElement | null, store: any): void {
  if (!target.startsWith('node:') || !el) return;
  const flow = document.querySelector('.react-flow')?.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  if (!flow) return;
  const inside = r.left >= flow.left && r.right <= flow.right && r.top >= flow.top && r.bottom <= flow.bottom;
  if (!inside) store.dispatch(centerOnNode(el.getAttribute('data-id') || ''));
}

const PointerOverlay: React.FC = () => {
  const store = useStore<RootState>();
  const handRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const [req, setReq] = useState<(PointRequest & { seq: number }) | null>(null);
  const [phase, setPhase] = useState<Phase>('hidden');
  const pos = useRef<{ x: number; y: number } | null>(null);
  const seq = useRef(0);

  // ── 요청 받기
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<PointRequest>).detail;
      if (!d || !d.target) { setReq(null); setPhase('hidden'); return; }
      seq.current += 1;
      setReq({ ...d, seq: seq.current });
    };
    window.addEventListener(POINT_EVENT, on);
    return () => window.removeEventListener(POINT_EVENT, on);
  }, []);

  // ── 한 번의 가리키기: 찾기 → 날아가기 → (누르기) → 머물기 → 사라지기
  useEffect(() => {
    if (!req) return undefined;
    let cancelled = false;
    let raf = 0;
    const timers: number[] = [];
    const later = (ms: number, f: () => void) => { timers.push(window.setTimeout(f, ms)); };
    let el: HTMLElement | null = null;
    let found = false;
    let finished = false;
    const finish = (ok: boolean) => { if (!finished) { finished = true; req.done?.(ok); } };
    let stopped = false;
    /** 물러나기 — 위치 따라가기도 멈춘다(보이지 않는데 매 프레임 계산하지 않게) */
    const hide = () => { stopped = true; cancelAnimationFrame(raf); setPhase('hidden'); pos.current = null; };

    const place = () => {
      if (cancelled || stopped) return;
      el = el && el.isConnected ? el : findElement(req.target, store);
      const hand = handRef.current; const ring = ringRef.current; const bubble = bubbleRef.current;
      if (el && hand && ring) {
        const r = el.getBoundingClientRect();
        const tipX = r.left + r.width / 2;
        const tipY = r.top + Math.min(r.height * 0.72, r.height - 4);
        const x = tipX - HAND * TIP_X;
        const y = tipY - HAND * 1.1 * TIP_Y;
        hand.style.transform = `translate(${x}px, ${y}px)`;
        pos.current = { x, y };
        const pad = 5;
        ring.style.transform = `translate(${r.left - pad}px, ${r.top - pad}px)`;
        ring.style.width = `${r.width + pad * 2}px`;
        ring.style.height = `${r.height + pad * 2}px`;
        ring.style.opacity = '';          // 보이고 숨는 것은 클래스(on)가 정한다 — 여기서 1 로 박으면 사라질 때 테두리만 남는다
        if (bubble) {
          const bw = bubble.offsetWidth || 180;
          const bx = Math.min(Math.max(8, x + HAND + 4), window.innerWidth - bw - 8);
          const by = Math.min(y + HAND * 0.55, window.innerHeight - (bubble.offsetHeight || 30) - 8);
          bubble.style.transform = `translate(${bx}px, ${by}px)`;
        }
      } else if (ring) {
        ring.style.opacity = '0';
      }
      raf = requestAnimationFrame(place);
    };

    // 대상이 조금 늦게 생길 수 있다(노드를 막 넣었거나 캔버스를 옮기는 중) — 잠시 찾아본다
    const start = Date.now();
    const seek = () => {
      if (cancelled) return;
      el = findElement(req.target, store);
      if (!el) {
        if (Date.now() - start < 1500) { later(100, seek); return; }
        hide(); finish(false); return;
      }
      found = true;
      const fly = Math.max(200, req.flyMs ?? FLY_MS);
      ensureNodeVisible(req.target, el, store);
      if (!req.target.startsWith('node:')) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      // 처음이면 오른쪽 아래에서 날아온다
      const hand = handRef.current;
      if (hand && !pos.current) hand.style.transform = `translate(${window.innerWidth - 90}px, ${window.innerHeight - 90}px)`;
      // 날아가는 시간 — 손가락과 말풍선이 같은 속도로 (CSS 변수 --of-fly)
      layerRef.current?.style.setProperty('--of-fly', `${fly}ms`);
      setPhase('fly');
      raf = requestAnimationFrame(place);
      later(fly, () => {
        if (req.tap) {
          setPhase('tap');
          later(TAP_MS, () => { setPhase('idle'); finish(true); });
        } else { setPhase('idle'); finish(true); }
      });
      const hold = req.hold ?? 6000;
      if (hold > 0) later(fly + (req.tap ? TAP_MS : 0) + hold, hide);
    };
    seek();

    // 사용자가 그 대상을 누르면(해 본 것) 물러난다 · Esc 로도
    const onDown = (e: PointerEvent) => { if (el && e.target instanceof Node && el.contains(e.target)) hide(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') hide(); };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      timers.forEach((t) => window.clearTimeout(t));
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey);
      finish(found);
    };
  }, [req, store]);

  const visible = phase !== 'hidden' && !!req;
  return (
    <div ref={layerRef} className="of-pointer-layer" aria-hidden>
      <div ref={ringRef} className={`of-pointer-ring ${visible ? 'on' : ''}`} />
      <div ref={handRef} className={`of-pointer-hand ${visible ? 'on' : ''} ${phase === 'fly' ? 'fly' : ''}`}>
        <div className={`of-pointer-hand-inner ${phase === 'tap' ? 'tap' : phase === 'idle' ? 'bob' : ''}`}>
          {/* @ts-ignore react-icons IconType 과 React 18 타입 불일치 회피 */}
          <FaHandPointer size={HAND} />
          {phase === 'tap' && <span className="of-pointer-ripple" />}
        </div>
      </div>
      {req?.label && <div ref={bubbleRef} className={`of-pointer-bubble ${visible ? 'on' : ''} ${phase === 'fly' ? 'fly' : ''}`}>{req.label}</div>}
    </div>
  );
};

export default PointerOverlay;
